/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import { Injectable } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse,
    HttpHeaders
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Interceptor all http requests and check response status code.
 */
@Injectable()
export class FcAgAuthenticationInterceptor implements HttpInterceptor {

    private tokenR: RegExp = /[^,\"\s]+/;
    
    private quotedStringR: RegExp = /\"[^\"]+\"/;
    
    private authParamR: string = this.concatRegExp(this.tokenR, '=(', this.tokenR, '|', this.quotedStringR, ')');
    
    private challengeR: string = this.concatRegExp(this.tokenR, this.commaSeparatedRegExp(this.authParamR));
    
    private challengeRegExp: RegExp = new RegExp(this.challengeR, 'g');

    /**
     * Default constructor.
     */
    constructor() {
    }
    
    private commaSeparatedRegExp(r: string): string {
        return this.concatRegExp('(', /\s+/, r, '(', /,\s*/, r, ')*)?');
    }
    
    private concatRegExp(...args: (string | RegExp)[]): string {
        let a: string = '';
        args.forEach((x) => {
            if (x instanceof RegExp) {
                x = x.toString();
                a += x.substring(1, x.lastIndexOf('/'));
            } else {
                a += x;
            }
        });
        return a;
    }
    
    private getAuthenticateResponseHeaders(headers: HttpHeaders): string[] {
        const header: string | null = (headers.get('WWW-Authenticate'));
        if (header) {
            return header.match(this.challengeRegExp) || [];
        }
        return [];
    }
    
    private parseAuthHeaderParams(paramsText: string): {[key: string]: string} {
        let parsed: {[key: string]: string} = {};
        paramsText = paramsText.trim();
        while (paramsText) {
            let params = paramsText.match(/([a-zA-Z]+)\s*=\s*"([^"]+)"(,.*)?/) || [];
            parsed[params[1]] = params[2];
            paramsText = params[3];
        }
        return parsed;
    }
    
    private extractParamAndRedirect(event: HttpErrorResponse) {
        let fcAuth: string[] = [];
        
        this.getAuthenticateResponseHeaders(event.headers).forEach((wwwAuthHeader) => {
            let splitHeader = wwwAuthHeader.trim().match(/^(\S+)\s+(.*)/) || [];
            if (splitHeader.length > 2 && splitHeader[1] === 'FC-Auth') {
                fcAuth = splitHeader;
            }
        });

        let loginUrl: string = '';

        if (fcAuth.length) {
            let authParams = this.parseAuthHeaderParams(fcAuth[2]);
            loginUrl = window.decodeURIComponent(authParams.browserLoginUrl);
        }

        if (loginUrl) {
            var currentUrl = window.location.href;

            if (currentUrl.includes('siteDocumentReview') || currentUrl.includes('siteDocumentUpload') 
                || currentUrl.includes('/NamirialAuditTrail/') || currentUrl.includes('/study-news-ui/')) {
                // Save current URL into local storage to handle redirect after login
                window.localStorage.setItem('redirectUrl', currentUrl);
            }
            window.location.href = loginUrl;
        }
    }
    
    /**
     * Interceptor all requests.
     * 
     * @param request Request interceptor.
     * @param next Http handler.
     * @returns Observable Observable object.
     */
    intercept(request:HttpRequest<any>, next:HttpHandler):Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((event:HttpErrorResponse) => {
                if (event instanceof HttpErrorResponse) {
                    if (event.status === 401) {
                        this.extractParamAndRedirect(event);
                    } else if (event.status === 500) {
                        //alert('An unknown error occurred.');
                    }
                }
                return throwError(event);
            })
        );
    }
}